import { Article } from '../Types/Article';
import { ArticleAttributeAssignment } from '../Types/ArticleAttributeAssignment';
import { CreatorFilter } from '../Types/CreatorFilter';
import { Question } from '../Types/Question';
import { UserArticle } from '../Types/UserArticle';
import {
  ARTICLE_IMAGE_RENDITIONS,
  generateAssetRenditionUrl,
  ImageFormat,
} from '../Utils/Assets';
import { getApiInstance } from './Api';

const mapQuestion = (question: any): Question => ({
  id: question.id,
  questionType: question.questionType,
  questionText: question.questionText,
  readingTip: question.readingTip,
  correctAnswerExplanation: question.correctAnswerExplanation,
  relatedPassageStartIndex: question.relatedPassageStartIndex,
  relatedPassageEndIndex: question.relatedPassageEndIndex,
  questionOptions: question.questionOptions,
  questionOptionExplanations: question.questionOptionExplanations,
  correctOptionIndex: question.correctOptionIndex,
  order: question.order ? question.order : 0,
});

export const mapArticle = (article: any, hasQuestions: boolean): Article => {
  const coverImageUrl =
    article.coverImagePath &&
    new URL(
      article.coverImagePath,
      window.env.REACT_APP_MEDIA_BASE_URL,
    ).toString();
  const coverVideoUrl =
    article.coverVideoPath &&
    new URL(
      article.coverVideoPath,
      window.env.REACT_APP_MEDIA_BASE_URL,
    ).toString();
  const contentVideoUrl =
    article.contentVideoPath &&
    new URL(
      article.contentVideoPath,
      window.env.REACT_APP_MEDIA_BASE_URL,
    ).toString();

  return {
    id: article.id,
    title: article.title,
    slug: article.slug,
    wordCount: article.wordCount,
    description: article.description,
    passage: article.passage,
    lexileLevel: article.lexileLevel,
    isFiction: article.isFiction,
    grade: article.grade,
    questions: hasQuestions ? article.questions.map(mapQuestion) : undefined,
    createdByUserId: article.createdByUserId,
    interests: article.interests,
    attributeAssignments: article.attributeAssignments?.map(
      mapAttributeAssignment,
    ),
    coverImage: coverImageUrl && {
      originalUrl: coverImageUrl,
      compressedUrl: generateAssetRenditionUrl(
        coverImageUrl,
        null,
        ImageFormat.WEBP,
      ),
      thumbnailUrl: generateAssetRenditionUrl(
        coverImageUrl,
        ARTICLE_IMAGE_RENDITIONS.THUMBNAIL,
        ImageFormat.WEBP,
      ),
      coverUrl: generateAssetRenditionUrl(
        coverImageUrl,
        ARTICLE_IMAGE_RENDITIONS.COVER,
        ImageFormat.WEBP,
      ),
    },
    coverVideo: coverVideoUrl && {
      url: coverVideoUrl,
    },
    contentVideo: contentVideoUrl && {
      url: contentVideoUrl,
    },
    createdAt: article.createdAt && new Date(Date.parse(article.createdAt)),
    updatedAt: article.updatedAt && new Date(Date.parse(article.updatedAt)),
    generatedAt:
      article.generatedAt && new Date(Date.parse(article.generatedAt)),
    challengingWordsGeneratedAt:
      article.challengingWordsGeneratedAt &&
      new Date(Date.parse(article.challengingWordsGeneratedAt)),
    quizGeneratedAt:
      article.quizGeneratedAt && new Date(Date.parse(article.quizGeneratedAt)),
    deletedAt: article.deletedAt && new Date(Date.parse(article.deletedAt)),
    coverImageTriggeredAt:
      article.coverImageTriggeredAt &&
      new Date(Date.parse(article.coverImageTriggeredAt)),
    coverImageGeneratedAt:
      article.coverImageGeneratedAt &&
      new Date(Date.parse(article.coverImageGeneratedAt)),
    coverVideoTriggeredAt:
      article.coverVideoTriggeredAt &&
      new Date(Date.parse(article.coverVideoTriggeredAt)),
    coverVideoGeneratedAt:
      article.coverVideoGeneratedAt &&
      new Date(Date.parse(article.coverVideoGeneratedAt)),
    contentVideoTriggeredAt:
      article.contentVideoTriggeredAt &&
      new Date(Date.parse(article.contentVideoTriggeredAt)),
    contentVideoGeneratedAt:
      article.contentVideoGeneratedAt &&
      new Date(Date.parse(article.contentVideoGeneratedAt)),
    status: article.status,
    isLibraryArticle: !article.createdByUserId,
  };
};

const mapAttributeAssignment = (
  attributeAssignment: any,
): ArticleAttributeAssignment => ({
  id: attributeAssignment.id,
  attributeId: attributeAssignment.attributeId,
  slot: attributeAssignment.slot,
  articleId: attributeAssignment.articleId,
});

export const mapUserArticle = (userArticle: any): UserArticle => ({
  article: mapArticle(userArticle.article, true),
  accuracy: userArticle.accuracy,
  state: userArticle.state,
  createdAt:
    userArticle.createdAt && new Date(Date.parse(userArticle.createdAt)),
  updatedAt:
    userArticle.updatedAt && new Date(Date.parse(userArticle.updatedAt)),
  questionsCompleted: userArticle.questionsCompleted,
});

export const fetchRecommendedArticles = async (userId: string) => {
  try {
    const response = await getApiInstance().get(
      `/articles/user/${userId}/recommended`,
    );
    const hasQuestions = false;
    const articles: Article[] = response.data.map((article: any) =>
      mapArticle(article, hasQuestions),
    );
    return articles;
  } catch (error) {
    console.error('Error fetching recommended articles', error);
    throw error;
  }
};

export const fetchArticle = async (articleId: string) => {
  try {
    const response = await getApiInstance().get(`/articles/${articleId}`);
    const hasQuestions = true;
    const article = mapArticle(response.data, hasQuestions);
    return article;
  } catch (error) {
    console.error('Error fetching article', error);
    throw error;
  }
};

export const fetchStandaloneUserArticles = async (
  userId: string,
  limit?: number,
  afterArticleId?: string,
  creatorFilter?: CreatorFilter,
  searchTerm?: string,
) => {
  try {
    let url = `/user-articles/${userId}`;
    const params = new URLSearchParams();

    if (limit) {
      params.append('limit', limit.toString());
    }

    if (afterArticleId) {
      params.append('afterArticleId', afterArticleId);
    }

    if (creatorFilter) {
      params.append('creatorFilter', creatorFilter);
    }

    if (searchTerm) {
      params.append('search', searchTerm);
    }

    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }

    const response = await getApiInstance().get(url);
    const userArticles = response.data.map((userArticle: any) =>
      mapUserArticle(userArticle),
    );
    return userArticles;
  } catch (error) {
    console.error('Error fetching user articles', error);
    throw error;
  }
};

export const touchUserArticle = async (userId: string, articleId: string) => {
  try {
    const response = await getApiInstance().post(
      `/user-articles/${userId}/${articleId}/touch`,
    );
    return response.data;
  } catch (error) {
    console.error('Error touching article', error);
    throw error;
  }
};

export const countStandaloneUserArticles = async (
  userId: string,
): Promise<number> => {
  try {
    const response = await getApiInstance().get(
      `/user-articles/${userId}/count`,
    );
    return response.data.count;
  } catch (error) {
    console.error('Error counting user articles', error);
    throw error;
  }
};

export const triggerUserArticleGeneration = async (params: {
  attributeIdsBySlot: {
    [key: string]: string[];
  };
  storyline: string;
  baseArticleId: string | undefined;
}): Promise<{ requestId: string; newArticleId: string }> => {
  const api = getApiInstance();
  const response = await api.post('/articles/generate/user/async', params);
  return response.data;
};

export const triggerArticleVideoCoverGeneration = async (
  articleId: string,
): Promise<{ requestId: string }> => {
  try {
    const response = await getApiInstance().post(
      `/video/article/${articleId}/cover-video`,
    );
    return response.data;
  } catch (error) {
    console.error('Error generating article video cover:', error);
    throw error;
  }
};

export const triggerGenerateArticleContentVideo = async (
  articleId: string,
): Promise<{ requestId: string }> => {
  try {
    const response = await getApiInstance().post(
      `/video/article/${articleId}/content-video`,
    );
    return response.data;
  } catch (error) {
    console.error('Error generating article content video:', error);
    throw error;
  }
};

export const fetchUserArticleGenerationStatus = async (
  requestId: string,
  articleId: string,
) => {
  try {
    const response = await getApiInstance().get(
      `/articles/generate/status?requestId=${requestId}&articleId=${articleId}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching user article generation status', error);
    throw error;
  }
};

export const fetchChallengingWords = async (articleId: string) => {
  try {
    const response = await getApiInstance().get(
      `/articles/${articleId}/challenging-words`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching challenging words', error);
    throw error;
  }
};
